/**
 * OverOnsPage — IMPERIA AI positioning als AI-first marketing PLATFORM.
 *
 * Niet als bureau-met-mensen, wél als product met menselijk vangnet.
 * SEO: H1 "Over IMPERIA AI", H2 missie/verschil/team, schema.org AboutPage + Organization.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import {
  Bot, ShieldCheck, Activity, Zap, Users, Crown, ArrowRight,
  CheckCircle2, Target, TrendingUp, Lock, Globe, MapPin,
} from 'lucide-react';

const PRINCIPES = [
  {
    icon: ShieldCheck,
    title: 'Eerlijke statussen. Altijd.',
    desc: 'Geen valse "uitgevoerd"-badges. U ziet exact: queued, wacht-op-koppeling, ops-team-plaatst, uitgevoerd met live-link. Liever eerlijk vertraagd dan vals afgevinkt.',
  },
  {
    icon: Bot,
    title: 'AI doet 80% — wij doen de andere 20%',
    desc: 'AI voert het standaardwerk uit (REST API push, snippet-generatie, content, ads-optimalisatie). Ons team handelt strategische beslissingen, snippets-met-context en team_handling-flows af.',
  },
  {
    icon: Lock,
    title: 'Geen lock-in. Geen jaarcontracten.',
    desc: 'Alle tiers maandelijks opzegbaar. Uw data blijft van u en is exporteerbaar. Wij leveren waarde óf u stopt — zo simpel.',
  },
  {
    icon: Activity,
    title: 'Realtime inzicht, geen black-box rapporten',
    desc: 'In uw dashboard ziet u élke wijziging die AI doet, met evidence-URL voor live verificatie. Maandelijkse rapporten zijn een samenvatting, niet een Excel-mysterie.',
  },
];

const VERSCHIL = [
  {
    label: 'Klassiek marketingbureau',
    rows: [
      'Factureert per uur of vaste maandelijkse fee voor "advies"',
      'U krijgt een PowerPoint met aanbevelingen',
      'Uitvoering = volgende factuur',
      '6-week intake, 12-maand contract',
      'Black-box rapportages',
      'Bureau "doet" — u weet niet wat',
    ],
    bad: true,
  },
  {
    label: 'IMPERIA AI',
    rows: [
      'Vaste prijs: €0 / €14,95 / €995,95 → €2.495,95',
      'AI voert ECHT uit via WP/Shopify-API',
      'Live evidence per wijziging',
      'Direct starten, maandelijks opzegbaar',
      'Realtime dashboard met statussen',
      'U keurt strategisch goed, AI voert uit',
    ],
    bad: false,
  },
];

const STATS = [
  { label: 'Directories in catalog', value: '80+' },
  { label: 'Backlinks/maand (Managed)', value: '25-40' },
  { label: 'Talen onder AI executor', value: 'NL · EN' },
  { label: 'Maandelijks opzegbaar', value: '100%' },
];

const TEAM = [
  {
    role: 'AI & Engineering',
    desc: 'Het AI-platform achter IMPERIA AI: execution-engine, cms_executor, snippet_generator, backlinks_service. Continue ontwikkeling met Claude Sonnet 4.5 en de Emergent LLM-key.',
  },
  {
    role: 'Ops & Klant-success',
    desc: 'Voor team_handling CMS-platforms (Joomla / Drupal / Mijndomein) plaatsen wij handmatig binnen 5 werkdagen SLA. Daarnaast: onboarding, strategie-gesprekken en account-management.',
  },
  {
    role: 'Content & Creatief',
    desc: 'AI levert eerste versies, ons redactie-team finetuned. Ads-creatief, blog-content, landingspages — altijd met menselijke kwaliteits-check.',
  },
];

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "url": "https://www.imperiaai.nl/over-ons",
    "mainEntity": {
      "@type": "Organization",
      "name": "IMPERIA AI",
      "url": "https://www.imperiaai.nl",
      "description": "AI-first marketing platform voor Nederlandse MKB. Wij voeren marketing uit met AI en menselijk vangnet — geen klassiek agency.",
      "address": { "@type": "PostalAddress", "addressLocality": "Zwolle", "addressCountry": "NL" },
    },
  },
];

export default function OverOnsPage() {
  return (
    <>
      <SEOHead
        title="Over IMPERIA AI · Uw AI Marketing Manager voor MKB & zzp"
        description="IMPERIA AI is uw AI Marketing Manager voor MKB & zzp in Nederland. Geen bureau met uren-facturen — een platform dat online vindbaarheid en lokale SEO daadwerkelijk uitvoert."
        canonical="/over-ons"
        keywords="over imperia ai, ai marketing manager mkb, marketingbureau alternatief mkb, online marketing zzp nederland, lokale seo specialist"
        schema={SCHEMA}
      />

      {/* ═══ HERO ═══ */}
      <section style={{
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)',
        padding: '90px 0 70px', color: 'white',
      }}>
        <div className="container-custom max-w-4xl mx-auto px-4 text-center">
          <span className="inline-flex items-center gap-2 bg-[#D4A017]/15 border border-[#D4A017]/30 rounded-full px-3 py-1.5 mb-6">
            <Crown size={12} className="text-[#D4A017]" />
            <span className="text-[#D4A017] text-xs font-bold tracking-wide uppercase">Over IMPERIA AI</span>
          </span>
          <h1
            data-testid="over-hero-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] mb-6"
            style={{ fontFamily: "'Sora', sans-serif" }}>
            Een marketing-<span style={{ color: '#D4A017' }}>platform</span>, geen marketing-<span style={{ textDecoration: 'line-through', opacity: 0.55 }}>bureau</span>
          </h1>
          <p className="text-white/75 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            IMPERIA AI bouwt aan een AI-first marketing-platform met menselijk vangnet. Wij voeren marketing daadwerkelijk uit — via REST API, snippet-generatie of ons ops-team — en u ziet realtime wat er gebeurt. Geen uren-rekeningen, geen vage rapportages, geen jaarcontracten.
          </p>
        </div>
      </section>

      {/* ═══ MISSIE ═══ */}
      <section style={{ background: 'white', padding: '70px 0 60px' }}>
        <div className="container-custom max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
              Onze missie
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 30, fontWeight: 800, color: '#0f172a', lineHeight: 1.2, marginBottom: 14 }}>
              Marketing toegankelijk maken voor élke Nederlandse MKB
            </h2>
            <p style={{ fontSize: 15, color: '#475569', lineHeight: 1.7, maxWidth: 720, margin: '0 auto' }}>
              De meeste ondernemers willen wél marketing — maar niet de tijd, het geld of het gedoe van een bureau. Wij bouwen een platform waarin AI het zware werk uitvoert (SEO-fixes, ads-optimalisatie, backlinks, content) en u alleen strategisch hoeft goed te keuren. Geen uren-rekeningen. Geen 6-week intakes. Geen vage beloftes.
            </p>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-10">
            {STATS.map((s) => (
              <div key={s.label} style={{
                background: '#f8fafc', borderRadius: 12, padding: '20px 16px', textAlign: 'center',
                border: '1px solid #e2e8f0',
              }}>
                <p style={{ fontFamily: "'Sora',sans-serif", fontSize: 28, fontWeight: 800, color: '#D4A017', lineHeight: 1 }}>{s.value}</p>
                <p style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginTop: 6, lineHeight: 1.3 }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRINCIPES ═══ */}
      <section style={{ background: '#f8fafc', padding: '70px 0 60px' }}>
        <div className="container-custom max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
              4 principes
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 30, fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>
              Wat ons anders maakt dan een klassiek bureau
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {PRINCIPES.map((p) => (
              <div key={p.title} style={{
                background: 'white', borderRadius: 14, padding: '24px 22px',
                border: '1px solid #e2e8f0',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #D4A017, #fbbf24)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <p.icon size={22} style={{ color: '#0f172a' }} />
                  </div>
                  <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 17, fontWeight: 800, color: '#0f172a' }}>{p.title}</h3>
                </div>
                <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ VERSCHIL bureau vs platform ═══ */}
      <section style={{ background: 'white', padding: '70px 0 60px' }}>
        <div className="container-custom max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
              Bureau vs. platform
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 30, fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>
              Een eerlijke vergelijking
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {VERSCHIL.map((col) => (
              <div key={col.label} style={{
                background: col.bad ? '#fef2f2' : '#f0fdf4',
                borderRadius: 14, padding: '24px 22px',
                border: `1px solid ${col.bad ? '#fecaca' : '#bbf7d0'}`,
              }}>
                <h3 style={{
                  fontFamily: "'Sora',sans-serif", fontSize: 17, fontWeight: 800,
                  color: col.bad ? '#b91c1c' : '#15803d',
                  marginBottom: 14,
                }}>
                  {col.label}
                </h3>
                <ul style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {col.rows.map((r, i) => (
                    <li key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: col.bad ? '#7f1d1d' : '#14532d', lineHeight: 1.5 }}>
                      <span style={{ color: col.bad ? '#dc2626' : '#16a34a', fontWeight: 800, flexShrink: 0 }}>
                        {col.bad ? '✗' : '✓'}
                      </span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOE WIJ WERKEN — team ═══ */}
      <section style={{ background: '#0f172a', padding: '80px 0', color: 'white' }}>
        <div className="container-custom max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
              Achter de schermen
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 30, fontWeight: 800, color: 'white', lineHeight: 1.15 }}>
              AI + mensen — niet of/of
            </h2>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', maxWidth: 580, margin: '0 auto', marginTop: 8, lineHeight: 1.55 }}>
              Het platform draait niet zonder mensen. AI doet het standaardwerk, ons team handelt de uitzonderingen, strategie en kwaliteit af.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TEAM.map((t) => (
              <div key={t.role} style={{
                background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: '22px 20px',
                border: '1px solid rgba(255,255,255,0.08)',
              }}>
                <Users size={22} style={{ color: '#D4A017', marginBottom: 10 }} />
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 16, fontWeight: 800, color: 'white', marginBottom: 6 }}>
                  {t.role}
                </h3>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ VOOR WIE ═══ */}
      <section style={{ background: '#f8fafc', padding: '70px 0 60px' }}>
        <div className="container-custom max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
              Voor wie wij bouwen
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 30, fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>
              Nederlandse MKB die echt willen groeien
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: MapPin, title: 'Lokale dienstverleners', desc: 'Bouw, horeca, kappers, garages, fysio\'s. Verbeter uw Google Bedrijfsprofiel en lokale ranking met AI.' },
              { icon: Globe, title: 'Webshops & retail', desc: 'Shopify of WooCommerce. Hogere conversie via SEO, Trustpilot, Kiyoh en geoptimaliseerde ads.' },
              { icon: Crown, title: 'Service-bedrijven', desc: 'Consultancy, IT, juridisch, marketing. Bouw autoriteit via content, backlinks en branche-directories.' },
            ].map((c) => (
              <div key={c.title} style={{
                background: 'white', borderRadius: 14, padding: '22px 20px',
                border: '1px solid #e2e8f0',
              }}>
                <c.icon size={22} style={{ color: '#D4A017', marginBottom: 10 }} />
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>{c.title}</h3>
                <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.55 }}>{c.desc}</p>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', marginTop: 24, fontSize: 13, color: '#64748b', maxWidth: 580, margin: '24px auto 0' }}>
            Niet voor: multinationals (te complex), hobby-projecten (geen omzet om te optimaliseren), en MLM/dropshipping (geen langetermijn-merken).
          </p>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section style={{ background: 'linear-gradient(135deg, #0f172a, #1e1b4b)', padding: '80px 0', color: 'white' }}>
        <div className="container-custom max-w-3xl mx-auto px-4 text-center">
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 32, fontWeight: 800, lineHeight: 1.15, marginBottom: 10 }}>
            Klaar om <span style={{ color: '#D4A017' }}>écht</span> aan de slag te gaan?
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', marginBottom: 28, maxWidth: 520, margin: '0 auto 28px', lineHeight: 1.55 }}>
            Start met een gratis online groeiscan en ervaar zelf het verschil tussen een platform en een bureau.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" style={{
              background: '#D4A017', color: '#0f172a', padding: '14px 28px',
              borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              Start gratis scan <ArrowRight size={14} />
            </Link>
            <Link to="/contact" style={{
              border: '1px solid rgba(255,255,255,0.3)', color: 'white',
              padding: '14px 28px', borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              Contact opnemen
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
